import fs from 'fs'
import path from 'path'

import { CONFIG_FILE, DEFAULT_SERVER_URL, SERVER_URL_ENV } from '../constants'

// `flowrunner.json` — the tracked half of a project's identity. It names the server and the workspace, and
// it belongs in git; the token that goes with it does not (see `./token`).
//
// Every function here takes the project root explicitly. It defaults to the current directory because that
// is what a CLI means, but a caller driving this from code is usually pointing at a project somewhere else.

export interface ProjectConfig {
  serverUrl?: string
  workspaceId?: string
  /** Cosmetic: which workspace the id refers to, so the CLI can name it instead of printing a GUID. */
  workspaceName?: string
  [key: string]: unknown
}

export interface EnsuredServerUrl {
  serverUrl: string
  /** `flowrunner.json` did not exist and was created. */
  created: boolean
  /** It existed but did not name a server, or named a different one, and was rewritten. */
  updated: boolean
}

export function configPath(projectRoot: string = process.cwd()): string {
  return path.resolve(projectRoot, CONFIG_FILE)
}

export function readConfig(projectRoot: string = process.cwd()): ProjectConfig {
  const fullPath = configPath(projectRoot)

  if (!fs.existsSync(fullPath)) {
    return {}
  }

  return JSON.parse(fs.readFileSync(fullPath, 'utf-8'))
}

export function writeConfig(config: ProjectConfig, projectRoot: string = process.cwd()): void {
  fs.writeFileSync(configPath(projectRoot), JSON.stringify(config, null, 2) + '\n', 'utf-8')
}

/**
 * Which console this project talks to: the project file first, then the environment, then the shipped
 * default. Never empty — a project that has never logged in still has somewhere to log in to.
 */
export function getServerUrl(projectRoot: string = process.cwd()): string {
  return readConfig(projectRoot).serverUrl || process.env[SERVER_URL_ENV] || DEFAULT_SERVER_URL
}

export function getWorkspaceId(projectRoot: string = process.cwd()): string {
  return readConfig(projectRoot).workspaceId || ''
}

/**
 * Make the project's server URL explicit in `flowrunner.json`, creating the file if it is missing.
 *
 * Leaving it implicit reads as working by accident: the project would behave differently depending on an
 * environment variable, or on which CLI version happens to be installed. `init` writes the choice down
 * once, and every command after that reads the project's own decision.
 */
export function ensureServerUrl(projectRoot: string = process.cwd(), serverUrl?: string): EnsuredServerUrl {
  const exists = fs.existsSync(configPath(projectRoot))
  const config = readConfig(projectRoot)
  const resolved = serverUrl || config.serverUrl || process.env[SERVER_URL_ENV] || DEFAULT_SERVER_URL

  if (exists && config.serverUrl === resolved) {
    return { serverUrl: resolved, created: false, updated: false }
  }

  writeConfig({ ...config, serverUrl: resolved }, projectRoot)

  return { serverUrl: resolved, created: !exists, updated: exists }
}
